/**
 * WatchFinder payout — what the store receives from a WatchFinder sale.
 *
 * WatchFinder keeps a 4.9% commission plus 20% VAT on that commission (VAT is
 * on the commission only, not on the WF price), and a flat £290 fee.
 * Everything here is GBP, major units.
 *   payout = price − price × 4.9% × 1.2 − £290
 * The suggested WatchFinder price is the reverse: the price whose payout equals
 * the store's own listing price, rounded up to the next whole pound.
 */
export const WF_COMMISSION = 0.049;
export const WF_VAT = 0.2;
export const WF_FEE_GBP = 290;

// Share of the WatchFinder price left after commission + VAT on it.
const KEEP = 1 - WF_COMMISSION * (1 + WF_VAT);

const positive = (v) => {
  const n = Number(String(v ?? "").replace(/[£€$₮,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Commission + VAT on it, for a WatchFinder price (GBP). */
export const wfCommission = (price) => {
  const p = positive(price);
  return p === null ? null : Math.round(p * WF_COMMISSION * (1 + WF_VAT));
};

/** What the store is paid for a WatchFinder price (GBP); null without a price. */
export const wfPayout = (price) => {
  const p = positive(price);
  return p === null ? null : Math.round(p * KEEP - WF_FEE_GBP);
};

/** The WatchFinder price whose payout is the store price (GBP); null without one. */
export const wfPriceForPayout = (storeGbp) => {
  const s = positive(storeGbp);
  return s === null ? null : Math.ceil((s + WF_FEE_GBP) / KEEP);
};

/**
 * The store listing price in GBP. `rates` is a useAllFx("GBP") table, where
 * rates[CODE] is the value of ONE unit of CODE in GBP — so CODE → GBP
 * multiplies. Null when there is no price (or Price on request) or no rate.
 */
export const storePriceGbp = (price, currency, rates) => {
  const p = positive(price);
  if (p === null) return null;
  const code = String(currency || "GBP").toUpperCase();
  if (code === "GBP") return p;
  const rate = Number(rates?.[code]);
  return Number.isFinite(rate) && rate > 0 ? Math.round(p * rate) : null;
};
